import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { hasRole } from "@/utils/roleUtils";
import { PROFILE_UPDATE_ERROR, SOCIAL_LINKS_SAVE_ERROR } from "@/utils/toastMessages";

export interface ProfileData {
  // v3: the unified user_profiles table only has `current_title` -- both
  // achiever and advisor profile GET/PUT responses use `currentTitle`.
  // (Previously achievers read/wrote a `currentRole` field the backend
  // AchieverProfileOut/Update schemas never had, so the field silently
  // stayed blank and saves 422'd.)
  currentTitle?: string;
  currentCompany?: string;
  currentRoleLevel?: string;
  location?: string;
  countryRegion?: string;
  primaryFunction?: string;
  bio?: string;
  profilePictureUrl?: string;
  linkedIn?: string;
  personalWebsite?: string;
  /** Single values from profile::job-role / profile::industry (signup step 2). */
  jobRole?: string;
  industry?: string;
  yearsOfExperience?: string;
  topGrowthAreas?: string[];
}

export interface AchieverProfileData extends ProfileData {
  topGrowthAreas?: string[];
  goalsAndChallenges?: string;
  organisationType?: string[];
}

export interface AdvisorProfileData extends ProfileData {
  experienceContext?: string[];
  organisationType?: string[];
  functionsAdvised?: string[];
  levelsMentored?: string[];
  publicSlug?: string;
  isPublic?: boolean;
}

export interface ProfileUser {
  id: string;
  role: string;
  roles?: string[];
  email: string;
  fullName: string;
}

function useAchieverId(enabled: boolean) {
  return useQuery({
    queryKey: ["achiever-id"],
    queryFn: async () => {
      const achiever = await api.achievers.getAchieverMe();
      return achiever.id;
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
}

function useAdvisorId(enabled: boolean) {
  return useQuery({
    queryKey: ["advisor-id"],
    queryFn: async () => {
      // v3: advisor identity IS the user_id (SHA256 string), not an int PK.
      const advisor = await api.advisors.getAdvisor();
      return advisor.userId;
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
}

export function useProfile(
  user: ProfileUser | null,
  enabled: boolean = true,
  roleContext?: "achiever" | "advisor",
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Dual-role users hold both roles at once, so `profile`/`refetch` can't be
  // resolved from role membership alone -- callers that know which profile
  // they mean (AdvisorProfilePage, AchieverProfilePage) must say so via
  // roleContext. Without it, fall back to preferring achiever (unchanged
  // behavior for callers that only ever read generic profile fields).
  const isAchiever = roleContext ? roleContext === "achiever" : hasRole(user, "achiever");
  const isAdvisor = roleContext ? roleContext === "advisor" : hasRole(user, "advisor");

  const achieverIdQuery = useAchieverId(enabled && isAchiever);
  const advisorIdQuery = useAdvisorId(enabled && isAdvisor);

  const achieverQuery = useQuery({
    queryKey: ["achiever-profile", achieverIdQuery.data],
    queryFn: () => api.achievers.getProfile(achieverIdQuery.data!),
    enabled: enabled && isAchiever && !!achieverIdQuery.data,
    retry: 1,
  });

  const advisorQuery = useQuery({
    queryKey: ["advisor-profile", advisorIdQuery.data],
    queryFn: () => api.advisors.getProfile(advisorIdQuery.data!),
    enabled: enabled && isAdvisor && !!advisorIdQuery.data,
    retry: 1,
  });
  const updateAchieverProfile = useMutation({
    mutationFn: async (data: Partial<AchieverProfileData>) => {
      // Get current profile
      const currentProfile = queryClient.getQueryData<Record<string, unknown> | null>([
        "achiever-profile",
        achieverIdQuery.data,
      ]);

      // Merge the update data with existing profile
      const mergedProfile = { ...currentProfile, ...data };

      return api.achievers.updateProfile(achieverIdQuery.data!, mergedProfile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achiever-profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Failed to update profile:", error);
      showToast({
        type: "error",
        message: PROFILE_UPDATE_ERROR,
      });
    },
  });

  const updateAchieverSocialLinks = useMutation({
    mutationFn: (data: { linkedIn?: string; personalWebsite?: string }) => {
      return api.achievers.updateSocialLinks(achieverIdQuery.data!, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achiever-profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Failed to update social links:", error);
      showToast({
        type: "error",
        message: SOCIAL_LINKS_SAVE_ERROR,
      });
    },
  });

  const updateAdvisorProfile = useMutation({
    mutationFn: async (data: Partial<AdvisorProfileData>) => {
      // Get current profile
      const currentProfile = queryClient.getQueryData<Record<string, unknown> | null>([
        "advisor-profile",
        advisorIdQuery.data,
      ]);

      // Merge the update data with existing profile
      const mergedProfile = { ...currentProfile, ...data };

      return api.advisors.updateProfileWithPreferences(mergedProfile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advisor-profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Failed to update profile:", error);
      showToast({
        type: "error",
        message: PROFILE_UPDATE_ERROR,
      });
    },
  });

  const updateAdvisorSocialLinks = useMutation({
    mutationFn: async (data: { linkedIn?: string; personalWebsite?: string }) => {
      // Get current profile
      const currentProfile = queryClient.getQueryData<Record<string, unknown> | null>([
        "advisor-profile",
        advisorIdQuery.data,
      ]);

      // Merge social links with existing profile
      const mergedProfile = { ...currentProfile, ...data };

      return api.advisors.updateProfileWithPreferences(mergedProfile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advisor-profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Failed to update social links:", error);
      showToast({
        type: "error",
        message: SOCIAL_LINKS_SAVE_ERROR,
      });
    },
  });

  const refetch = isAchiever ? achieverQuery.refetch : advisorQuery.refetch;

  return {
    profile: isAchiever ? achieverQuery.data : advisorQuery.data,
    isLoading: isAchiever 
      ? (achieverIdQuery.isLoading || achieverQuery.isLoading)
      : (advisorIdQuery.isLoading || advisorQuery.isLoading),
    isError: isAchiever 
      ? (achieverIdQuery.isError || achieverQuery.isError)
      : (advisorIdQuery.isError || advisorQuery.isError),
    error: isAchiever 
      ? (achieverIdQuery.error || achieverQuery.error)
      : (advisorIdQuery.error || advisorQuery.error),
    updateSocialLinks: isAchiever ? updateAchieverSocialLinks : updateAdvisorSocialLinks,
    updateProfile: isAchiever ? updateAchieverProfile : updateAdvisorProfile,
    refetch,
  };
}